import { FC, useEffect } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FieldError, FieldValues, FormProvider, Resolver, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
	useWriteSurveyAnswersMutation,
	useLazyGetFamilySurveyTrackQuery,
	useLazyCheckExistSurveyAnswerQuery,
	useLazyGetEngagementInformationBySurveyQuery,
	useLazyGetEngagementSurveyByIdQuery,
} from '../../../../store/api/survey.api';
import { SurveyQuestionRender } from '../../components/question/question.component';
import { SurveyHeader } from '../../../_core/components/header-survey/header-survey.component';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import { SurveyAnswerCompletedByEnum, SurveyQuestionInterface, SurveyQuestionTypeEnum } from '@families-link/shared';

const selectLanguageObject: { [key: string]: string } = {
	Arabic: 'ar',
	ar: 'ar',
	Spanish: 'es',
	es: 'es',
};

export const SurveyViewPage: FC = () => {
	const { t, i18n } = useTranslation('survey');
	const [triggerGetFamilySurveyTrackQuery, { data: familyTrack }] = useLazyGetFamilySurveyTrackQuery();
	const [triggerGetEngagementSurveyByIdQuery, { data: survey, isError, isFetching }] = useLazyGetEngagementSurveyByIdQuery();
	const [triggerCheckExistSurveyAnswerQuery, { data: surveyAnswers }] = useLazyCheckExistSurveyAnswerQuery();
	const [triggerGetEngagementInformationBySurveyQuery, { data: engagementInformation }] =
		useLazyGetEngagementInformationBySurveyQuery();
	const [writeSurveyAnswers] = useWriteSurveyAnswersMutation();

	const { urlHash, familyId: familyIdParam, surveyId: surveyIdParam } = useParams();

	useEffect(() => {
		if (urlHash) {
			triggerGetFamilySurveyTrackQuery(urlHash);
		}
	}, [urlHash, triggerGetFamilySurveyTrackQuery]);
	const familyId = familyTrack?.familyId ?? familyIdParam;
	const surveyId = familyTrack?.engagementSurveyId ?? surveyIdParam;

	useEffect(() => {
		if (surveyId) {
			triggerGetEngagementInformationBySurveyQuery(surveyId);
		}

		if (surveyId && familyId) {
			triggerGetEngagementSurveyByIdQuery(surveyId);
			triggerCheckExistSurveyAnswerQuery({
				surveyId,
				familyId,
			});
		}
	}, [
		surveyId,
		familyId,
		triggerCheckExistSurveyAnswerQuery,
		triggerGetEngagementSurveyByIdQuery,
		triggerGetEngagementInformationBySurveyQuery,
	]);

	const browserLanguage = navigator.language;

	const templateLanguageArabicOrSpanishArray = ['Arabic', 'Spanish'];
	const browserLanguageArabicOrSpanishArray = ['ar', 'es'];

	const surveyTemplate = survey?.surveyTemplate;
	const surveyTranslate = survey?.surveyTemplate?.translates?.find((translate)=>translate.language === survey.engagementLanguage);
		console.log(surveyTranslate)

	const selectedLanguage =
		surveyTranslate &&
		templateLanguageArabicOrSpanishArray.includes(surveyTranslate.language)
			? selectLanguageObject[surveyTranslate.language]
			: browserLanguageArabicOrSpanishArray.includes(browserLanguage)
			? selectLanguageObject[browserLanguage]
			: 'en';

	useEffect(() => {
		i18n.changeLanguage(selectedLanguage);
	}, [i18n, surveyTranslate?.language, browserLanguage]);

	const isManualChanged =
		familyIdParam && surveyIdParam ? SurveyAnswerCompletedByEnum.Manual : SurveyAnswerCompletedByEnum.byUser;
	const defaultValues = {
		familyId,
		surveyId,
		completedBy: isManualChanged,
		...surveyTemplate?.questions?.reduce((result: any, el: SurveyQuestionInterface) => {
			result[`field_${el._id}`] = '';

			return result;
		}, {}),
	};

	const customResolver: Resolver = async (values: FieldValues, context: { surveyTemplate: any }) => {
		const errors: { [key: string]: FieldError } = {};

		if (context.surveyTemplate) {
			context.surveyTemplate.questions.forEach((question: SurveyQuestionInterface) => {
				switch (question.type) {
					case SurveyQuestionTypeEnum.Options: {
						const fieldName = `option_field_${question._id}`;
						const additionalFieldName = `additional_option_field_${question._id}`;
						const fieldValue = values[fieldName];
						const additionalFieldValue = values[additionalFieldName];
						if (!fieldValue) {
							if (question.isRequired) {
								errors[fieldName] = {
									type: 'required',
									message: t('survey.forms.errors.required-field'),
								};
							}
						} else if (JSON.parse(fieldValue)?.isOtherOption && !additionalFieldValue) {
							errors[additionalFieldName] = {
								type: 'required',
								message: 'You selected an option that needs more information. Please fill the field.',
							};
						}

						break;
					}
					case SurveyQuestionTypeEnum.MultiSelect: {
						const fieldName = `option_field_${question._id}`;
						const additionalFieldName = `additional_option_field_${question._id}`;
						const fieldValue = values[fieldName];
						const additionalFieldValue = values[additionalFieldName];
						if (Array.isArray(fieldValue) && fieldValue.length === 0) {
							if (question.isRequired) {
								errors[fieldName] = {
									type: 'required',
									message: t('survey.forms.errors.required-field'),
								};
							}
						} else if (
							Array.isArray(fieldValue) &&
							fieldValue.find((value: string) => JSON.parse(value)?.isOtherOption) &&
							!additionalFieldValue
						) {
							errors[additionalFieldName] = {
								type: 'required',
								message: 'You selected an option that needs more information. Please fill the field.',
							};
						}

						break;
					}
					default: {
						const fieldName = `field_${question._id}`;
						const fieldValue = values[fieldName];

						if (question.isRequired) {
							if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
								errors[fieldName] = {
									type: 'required',
									message: t('survey.forms.errors.required-field'),
								};
							}
						}

						break;
					}
				}
			});
		}

		return {
			values,
			errors,
		};
	};

	const methods = useForm({
		defaultValues,
		resolver: async (values, fieldNames) => customResolver(values, { surveyTemplate }, fieldNames),
		mode: 'onSubmit',
	});

	const {
		handleSubmit,
		formState: { isDirty, isLoading, isSubmitting, isSubmitted, isValid, errors },
	} = methods;

	const disableSubmit = !isValid && (isDirty || isSubmitted);
	const loadingSubmit = isLoading || isSubmitting;

	const onSubmit = async (values: any) => {
		const transformValues = Object.keys(values).reduce(
			(submitObject: any, key: string) => {
				if (key.startsWith('field_')) {
					const fieldId = key.replace('field_', '');
					submitObject['answers'].push({
						fieldId,
						value: { text: values[key] },
					});
				} else if (key.startsWith('option_field_')) {
					const fieldId = key.replace('option_field_', '');
					submitObject['answers'].push({
						fieldId,
						value: !values[key]
							? values[key]
							: Array.isArray(values[key])
							? values[key].map((el: any) => ({
									text: JSON.parse(el)?.translatedText,
									optionId: JSON.parse(el)?._id,
									additionalText: JSON.parse(el)?.isOtherOption
										? values[`additional_option_field_${fieldId}`]
										: undefined,
							  }))
							: {
									text: JSON.parse(values[key])?.translatedText,
									optionId: JSON.parse(values[key])?._id,
									additionalText: JSON.parse(values[key])?.isOtherOption
										? values[`additional_option_field_${fieldId}`]
										: undefined,
							  },
					});
				} else if (key.startsWith('additional_option_field_')) {
					//skip
				} else {
					submitObject[key] = values[key];
				}
				submitObject['familyId'] = familyId;
				submitObject['surveyId'] = surveyId;
				submitObject['language'] = surveyTranslate?.language ?? 'English';
				return submitObject;
			},
			{ answers: [] }
		);
		await writeSurveyAnswers(transformValues);
	};

	if (!familyId || !surveyId) {
		return null;
	}

	return (
		<Box
			sx={{
				display: 'flex',
				flex: 1,
				justifyContent: 'center',
				minHeight: 0,
				height: '100%',
				direction: selectedLanguage === 'ar' ? 'rtl' : 'ltr',
				textAlign: selectedLanguage === 'ar' ? 'right' : 'left',
			}}
		>
			{isError ? (
				<Box sx={{ width: '100%', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<Typography>{t('survey-templates.deleted-survey-template')}</Typography>
				</Box>
			) : !isFetching && surveyTemplate ? (
				<Box sx={{ width: '100%', height: '100%' }}>
					<Box component="header" sx={{ mb: 1 }}>
						<SurveyHeader
							title={surveyTemplate?.title}
							engagementType={engagementInformation?.type}
							engagementDuration={engagementInformation?.duration}
							engagementSchool={engagementInformation?.school}
						/>
					</Box>
					{surveyAnswers ? (
						<Paper elevation={0} sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
							<Typography fontSize={14} sx={{ fontWeight: '500' }}>
								{t('survey.completed-survey')}
							</Typography>
						</Paper>
					) : (
						<FormProvider {...methods}>
							<Box
								component="form"
								onSubmit={handleSubmit(onSubmit)}
								sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
							>
								{surveyTemplate?.questions?.map((question: any, index: number) => {
									const translatedQuestion = surveyTranslate?.isFinished && isManualChanged !== SurveyAnswerCompletedByEnum.Manual ? surveyTranslate?.questions?.find(
										(translatedQuestion: any) => translatedQuestion.questionId === question._id
									): undefined;

									return (
										<Paper
											key={question._id.toString()}
											elevation={0}
											sx={{ p: 3, display: 'flex', flexDirection: 'column' }}
										>
											<SurveyQuestionRender
												question={question}
												translatedQuestion={translatedQuestion}
												customLabel={`${index + 1}. ${
													translatedQuestion ? translatedQuestion.translatedText : question.text
												}`}
											/>
										</Paper>
									);
								})}
								<Box>
									<CoreButton type="submit" contraModeStyleButton disabled={disableSubmit} loading={loadingSubmit}>
										{t('survey.forms.buttons.submit')}
									</CoreButton>
								</Box>
							</Box>
						</FormProvider>
					)}
				</Box>
			) : null}
		</Box>
	);
};
